import {
  forwardRef,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
} from "react";

export const VALUT_INPUT_CLASS = "valut-input h-11 w-full rounded-xl";
export const VALUT_LABEL_CLASS = "mb-2 block text-sm font-medium text-gray-500";

export interface ValutFieldProps {
  label?: string;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

export function ValutField({ label, htmlFor, children, className }: ValutFieldProps) {
  return (
    <div className={className ?? "w-full"}>
      {label ? (
        <label htmlFor={htmlFor} className={VALUT_LABEL_CLASS}>
          {label}
        </label>
      ) : null}
      {children}
    </div>
  );
}

export interface ValutInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  startAdornment?: ReactNode;
}

export const ValutInput = forwardRef<HTMLInputElement, ValutInputProps>(
  ({ className = "", startAdornment, ...props }, ref) => {
    if (startAdornment) {
      return (
        <div className="relative flex w-full items-center">
          <span className="pointer-events-none absolute left-3 text-sm text-gray-500">
            {startAdornment}
          </span>
          <input
            ref={ref}
            className={`${VALUT_INPUT_CLASS} pl-8 ${className}`.trim()}
            {...props}
          />
        </div>
      );
    }

    return (
      <input ref={ref} className={`${VALUT_INPUT_CLASS} ${className}`.trim()} {...props} />
    );
  }
);

ValutInput.displayName = "ValutInput";

export interface ValutSelectOption {
  value: string | number;
  label: ReactNode;
}

export interface ValutSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options?: ValutSelectOption[];
}

export const ValutSelect = forwardRef<HTMLSelectElement, ValutSelectProps>(
  ({ className = "", options, children, ...props }, ref) => (
    <select
      ref={ref}
      className={`${VALUT_INPUT_CLASS} appearance-none !pr-10 ${className}`.trim()}
      {...props}
    >
      {options
        ? options.map((option) => (
            <option key={String(option.value)} value={option.value}>
              {option.label}
            </option>
          ))
        : children}
    </select>
  )
);

ValutSelect.displayName = "ValutSelect";

export function menuItemsToSelectOptions(children: ReactNode): ValutSelectOption[] {
  return (Array.isArray(children) ? children : [children])
    .filter((child): child is ReactElement => Boolean(child && typeof child === "object" && "props" in child))
    .map((child) => ({
      value: child.props.value ?? "",
      label: child.props.children,
    }));
}
